import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * NEXORA AI Platform
 * AuthProvider Component
 * 
 * Manages global authentication state
 * Handles session verification, user data, and auth state
 */
export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Define public routes that don't need auth check
    const publicRoutes = ['/', '/login', '/register', '/forgot-password', '/showcase'];
    const currentPath = window.location.pathname;
    
    // PERFORMANCE: Skip /me check on public routes only
    if (publicRoutes.includes(currentPath)) {
      setLoading(false);
      return;
    }
    
    // For all protected routes, verify authentication
    checkAuth();
  }, [checkAuth]);

  // Login with email/password
  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password, remember_me: rememberMe },
      { withCredentials: true }
    );
    
    // Backend returns user data directly, not wrapped in {user: ...}
    setUser(response.data);
    setIsAuthenticated(true);
    
    // Check if onboarding is completed
    return {
      user: response.data,
      requiresOnboarding: !response.data.onboarding_completed
    };
  };

  // Register new user
  const register = async (name, email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    
    // Backend returns user data directly, not wrapped in {user: ...}
    setUser(response.data);
    setIsAuthenticated(true);
    
    // New users always need onboarding
    return {
      user: response.data,
      requiresOnboarding: true
    };
  };

  // Logout
  const logout = async () => {
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Request password reset
  const requestPasswordReset = async (email) => {
    const response = await axios.post(
      `${API_URL}/api/auth/forgot-password`,
      { email }
    );
    return response.data;
  };

  // Reset password with token
  const resetPassword = async (token, newPassword) => {
    const response = await axios.post(
      `${API_URL}/api/auth/reset-password`,
      { token, new_password: newPassword }
    );
    return response.data;
  };

  // Update user data (after OAuth callback)
  const setAuthUser = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setLoading(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    requestPasswordReset,
    resetPassword,
    setAuthUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
